import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, ShieldCheck, Award, Users, Play } from 'lucide-react';

export default function HeroSection() {
  return (
    <section id="home" className="relative pt-10 pb-20 px-6 max-w-7xl mx-auto font-sans overflow-hidden">
      
      {/* Background Canvas Chart from Zip File Structure */}

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        
        {/* Left Hero Content */}
        <div className="lg:col-span-6 space-y-6">
          
          {/* Top Pill Badge */}
          <div className="inline-flex items-center gap-2 bg-[#EEF4FA] border border-[#C7D7E8] text-[#16325B] text-xs font-semibold px-3.5 py-1.5 rounded-full">
            <CheckCircle2 className="w-4 h-4 text-[#16325B]" />
            Ethical wealth guidance · Financial education
          </div>

          {/* Main Title */}
          <h1 className="font-serif-title text-5xl sm:text-6xl font-extrabold text-[#0B2540] leading-[1.12] tracking-tight">
            Money, <span className="text-[#16325B] italic font-normal">understood<span className="text-[#0B2540] font-normal font-sans">.</span></span><br />
            Wealth, built with<br />
            intention.
          </h1>

          {/* Subtitle */}
          <p className="text-gray-600 text-base sm:text-lg max-w-xl leading-relaxed">
            Welvate pairs structured, beginner-friendly learning with honest wealth guidance from Haritika — so every rupee you invest has a reason behind it.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center gap-4 pt-2">
            <a 
              href="#learning" 
              className="bg-[#16325B] hover:bg-[#0F213D] text-white font-bold px-7 py-3.5 rounded-2xl flex items-center gap-3 transition-all duration-300 shadow-md shadow-[#16325B]/20 text-sm"
            >
              Browse Learning Paths <ArrowRight className="w-4 h-4" />
            </a>
            
            <a 
              href="#method" 
              className="border border-gray-300 hover:border-gray-400 bg-white text-[#0B2540] font-bold px-6 py-3.5 rounded-2xl flex items-center gap-3 transition shadow-sm text-sm"
            >
              <div className="w-7 h-7 rounded-full border border-gray-400 flex items-center justify-center">
                <Play className="w-3.5 h-3.5 fill-[#0B2540] ml-0.5 text-[#0B2540]" />
              </div>
              The Welvate Method
            </a>
          </div>

          {/* Trust Badges Row */}
          <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200/80">
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full border border-gray-200 bg-white flex items-center justify-center text-[#16325B] shrink-0">
                <ShieldCheck className="w-5 h-5 stroke-[1.8]" />
              </div>
              <div>
                <h4 className="font-bold text-xs text-[#0B2540]">Trusted guidance</h4>
                <p className="text-[11px] text-gray-500 leading-tight">ethical & research-driven</p>
              </div>
            </div>

            <div className="flex items-center gap-3 border-l border-gray-200/80 pl-2">
              <div className="w-10 h-10 rounded-full border border-gray-200 bg-white flex items-center justify-center text-[#16325B] shrink-0">
                <Award className="w-5 h-5 stroke-[1.8]" />
              </div>
              <div>
                <h4 className="font-bold text-xs text-[#0B2540]">Certified expert</h4>
                <p className="text-[11px] text-gray-500 leading-tight">NISM ×2 · IRDAI</p>
              </div>
            </div>

            <div className="flex items-center gap-3 border-l border-gray-200/80 pl-2">
              <div className="w-10 h-10 rounded-full border border-gray-200 bg-white flex items-center justify-center text-[#16325B] shrink-0">
                <Users className="w-5 h-5 stroke-[1.8]" />
              </div>
              <div>
                <h4 className="font-bold text-xs text-[#0B2540]">100% Educational</h4>
                <p className="text-[11px] text-gray-500 leading-tight">we do not sell tips</p>
              </div>
            </div>

          </div>
        </div>

        {/* Right Section: Zip File Exact "Your Learning Space" Widget */}
        <div className="lg:col-span-6">
          <div className="bg-white rounded-[32px] p-8 shadow-2xl border border-gray-100 font-sans text-[#0B1E2D] max-w-[620px] ml-auto">
            
            {/* Widget Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="font-extrabold text-[#0B1E2D] text-2xl tracking-tight leading-none mb-1.5">
                  Your Learning Space
                </h3>
                <p className="text-[#16325B] text-xs font-semibold">
                  Small steps, compounding daily.
                </p>
              </div>

              <div className="text-xs font-medium text-[#667085] flex items-center gap-1.5 pt-0.5">
                <span className="inline-block border border-gray-300 rounded px-1.5 py-0.5 text-[10px] font-bold text-gray-500">1</span>
                <span>Day <strong className="text-[#0B1E2D] font-extrabold">7</strong> of your streak</span>
              </div>
            </div>

            {/* 2-Column Outer Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              
              {/* Left Column: Mutual Funds Progress Box */}
              <div className="bg-white border border-gray-200/80 rounded-2xl p-5 flex flex-col justify-between space-y-5">
                <div>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#EEF4FA] flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5 stroke-[#16325B] fill-none stroke-[2]" viewBox="0 0 24 24">
                        <path d="M12 22V12M12 12C12 7 7 4 2 4c0 5 3 10 10 10zm0 0c0-5 5-8 10-8 0 5-3 10-10 10z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-[#0B1E2D] leading-snug">
                        Mutual Funds & Index Funds
                      </h4>
                      <p className="text-[11px] text-[#667085] font-medium mt-0.5">
                        Beginner · 12 Lessons
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar & Percentage */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-[#EAECF0] h-2 rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: "65%" }}
                          transition={{ duration: 1.2, ease: "easeOut" }}
                          className="bg-[#16325B] h-full rounded-full"
                        />
                      </div>
                      <span className="text-[11px] font-bold text-[#667085] shrink-0">
                        65% Complete
                      </span>
                    </div>
                  </div>
                </div>

                {/* Up Next Banner */}
                <div className="bg-[#EEF4FA] rounded-xl p-3.5 flex items-center justify-between gap-2">
                  <div>
                    <span className="text-[9px] font-bold text-[#16325B] uppercase tracking-wider block mb-0.5">
                      UP NEXT
                    </span>
                    <p className="text-xs font-bold text-[#0B1E2D] leading-tight">
                      Reading a fund factsheet: what actually matters
                    </p>
                  </div>
                  
                  <div className="w-8 h-8 rounded-full border border-[#16325B] bg-white flex items-center justify-center shrink-0">
                    <svg className="w-3.5 h-3.5 fill-[#16325B] ml-0.5" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>

                {/* Lessons Finished Checklist */}
                <div className="space-y-2.5 text-xs font-medium text-[#667085] pt-1">
                  <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                    <span className="flex items-center gap-1.5">
                      <span className="text-[#16325B] font-bold">✓</span> Lessons finished
                    </span>
                    <span className="font-bold text-[#0B1E2D]">08</span>
                  </div>

                  <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                    <span className="flex items-center gap-1.5">
                      <span className="text-[#16325B] font-bold">↺</span> In progress
                    </span>
                    <span className="font-bold text-[#0B1E2D]">02</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-1.5">
                      <span className="text-[#667085]">○</span> Still ahead
                    </span>
                    <span className="font-bold text-[#0B1E2D]">02</span>
                  </div>
                </div>
              </div>

              {/* Right Column: Consistency Curve Chart */}
              <div className="bg-white border border-gray-200/80 rounded-2xl p-5 flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-0.5">
                    <h5 className="font-bold text-sm text-[#0B1E2D]">
                      Consistency Curve
                    </h5>
                    <span className="text-[10px] font-semibold text-[#344054] bg-[#F2F4F7] border border-gray-200 px-2 py-0.5 rounded-md flex items-center gap-1 cursor-pointer">
                      This Month <span className="text-[8px]">▼</span>
                    </span>
                  </div>
                  <p className="text-[11px] text-[#667085] mb-5">
                    Learning compounds — so does money.
                  </p>

                  {/* SVG Consistency Graph */}
                  <div className="relative h-28 w-full mt-2">
                    <div className="absolute inset-0 flex flex-col justify-between text-[9px] text-[#98A2B3] pointer-events-none pr-1">
                      <div className="border-b border-gray-100 w-full flex justify-between"><span>75%</span></div>
                      <div className="border-b border-gray-100 w-full flex justify-between"><span>50%</span></div>
                      <div className="border-b border-gray-100 w-full flex justify-between"><span>0%</span></div>
                    </div>

                    <svg className="w-full h-full overflow-visible relative z-10" viewBox="0 0 100 50" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="exactChartGradientMatch" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#16325B" stopOpacity="0.15" />
                          <stop offset="100%" stopColor="#16325B" stopOpacity="0.0" />
                        </linearGradient>
                      </defs>

                      <motion.path 
                        d="M 0,42 L 25,38 L 50,33 L 95,12 L 95,50 L 0,50 Z" 
                        fill="url(#exactChartGradientMatch)"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1 }}
                      />

                      <motion.path 
                        d="M 0,42 L 25,38 L 50,33 L 95,12" 
                        fill="none" 
                        stroke="#16325B" 
                        strokeWidth="1.5" 
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 1.5, ease: "easeInOut" }}
                      />

                      <motion.circle 
                        cx="95" 
                        cy="12" 
                        r="2.5" 
                        fill="#16325B" 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 1.4 }}
                      />
                    </svg>

                    <motion.div 
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 1.5, type: "spring" }}
                      className="absolute top-[2px] right-[-2px] bg-white border border-gray-200 text-[#0B1E2D] text-[9px] font-bold px-1.5 py-0.5 rounded shadow-sm"
                    >
                      65%
                    </motion.div>
                  </div>

                  <div className="flex justify-between text-[10px] text-[#98A2B3] font-medium mt-3">
                    <span>May 1</span>
                    <span>May 15</span>
                    <span>May 29</span>
                  </div>
                </div>

                <div className="bg-[#EEF4FA] rounded-xl p-2 flex justify-between items-center text-[10px] font-bold text-[#16325B]">
                  <span className="flex items-center gap-1.5 leading-snug">
                    <span>⏱</span>
                    <span>
                      6h 25m invested<br />
                      <span className="font-semibold text-[#16325B]/70">in yourself this month</span>
                    </span>
                  </span>

                  <a href="#breakdown" className="hover:underline flex items-center gap-0.5 text-[#0B1E2D] font-bold shrink-0">
                    See breakdown →
                  </a>
                </div>

              </div>

            </div>

          </div>
        </div>

      </div>
    </section>
  );
}